import * as THREE from 'three';

export default class Coin {
    constructor(scene, physics, position = { x: 0, y: 1, z: 0 }) {
        this.scene = scene;
        this.physics = physics;
        this.position = position;
        
        // Coin properties
        this.value = 1;
        this.collected = false;
        this.radius = 0.5;
        this.mesh = null;
        this.collider = null;
        
        // Create the coin visual
        this.create();
    }
    
    create() {
        // Create a gold coin using a cylinder
        const geometry = new THREE.CylinderGeometry(this.radius, this.radius, 0.1, 32);
        const material = new THREE.MeshStandardMaterial({ 
            color: 0xFFD700,
            metalness: 1,
            roughness: 0.3,
            emissive: 0xFFAA00,
            emissiveIntensity: 0.2
        });
        
        this.mesh = new THREE.Mesh(geometry, material);
        this.mesh.position.set(this.position.x, this.position.y, this.position.z);
        
        // Rotate to be flat like a coin
        this.mesh.rotation.x = Math.PI / 2;
        
        // Add to scene
        this.scene.add(this.mesh);
        
        // Create physics trigger volume
        if (this.physics && this.physics.isInitialized) {
            this.createPhysicsTrigger();
        }
        
        // Animate the coin
        this.startAnimation();
    }
    
    createPhysicsTrigger() {
        // Create a simple trigger that doesn't actually collide
        // but can be detected for overlap
        const triggerWidth = this.radius * 2;
        const triggerHeight = 0.5;
        const triggerDepth = this.radius * 2;
        
        // Create a physics trigger with proper dimensions
        this.collider = this.physics.createTrigger(
            new THREE.Vector3(this.position.x, this.position.y, this.position.z),
            { width: triggerWidth, height: triggerHeight, depth: triggerDepth },
            this // The userData will be this coin object
        );
        
        // Ensure the collider has proper halfExtents for collision detection
        if (this.collider && !this.collider.halfExtents) {
            this.collider.halfExtents = new THREE.Vector3(
                triggerWidth / 2,
                triggerHeight / 2,
                triggerDepth / 2
            );
        }
    }
    
    startAnimation() {
        // Set initial animation properties
        this.rotationSpeed = 1.5; // radians per second
        this.floatAmplitude = 0.2; // how high it floats up and down
        this.floatFrequency = 1.2; // cycles per second
        this.initialY = this.position.y;
        
        // Animation will be handled by the update method
    }
    
    update(deltaTime) {
        if (this.collected || !this.mesh) return;
        
        // Rotate the coin
        this.mesh.rotation.z += this.rotationSpeed * deltaTime;
        
        // Float up and down
        const floatOffset = Math.sin(performance.now() / 1000 * this.floatFrequency) * this.floatAmplitude;
        this.mesh.position.y = this.initialY + floatOffset;
        
        // If we have a physics collider, update its position too
        if (this.collider) {
            this.collider.position.y = this.initialY + floatOffset;
        }
    }
    
    collect() {
        if (this.collected) return;
        
        this.collected = true;
        
        // Play collection animation
        this.playCollectAnimation();
        
        // Emit a custom event that can be listened to by the game
        const collectEvent = new CustomEvent('coin_collected', {
            detail: {
                value: this.value,
                position: this.position
            }
        });
        
        window.dispatchEvent(collectEvent);
    }
    
    playCollectAnimation() {
        // Store a reference to this for later
        const self = this;
        
        // Play a quick scale-up and fade out animation
        const startScale = this.mesh.scale.x;
        const duration = 0.3; // seconds
        const startTime = performance.now();
        
        function animate() {
            const elapsedTime = (performance.now() - startTime) / 1000;
            const progress = Math.min(elapsedTime / duration, 1);
            
            // Scale up
            const newScale = startScale * (1 + progress);
            self.mesh.scale.set(newScale, newScale, newScale);
            
            // Fade out
            self.mesh.material.opacity = 1 - progress;
            self.mesh.material.transparent = true;
            
            if (progress < 1) {
                requestAnimationFrame(animate);
            } else {
                // Remove the coin when animation is complete
                self.remove();
            }
        }
        
        // Start the animation
        animate();
    }
    
    remove() {
        // Remove from scene
        if (this.mesh) {
            this.scene.remove(this.mesh);
            this.mesh.geometry.dispose();
            this.mesh.material.dispose();
            this.mesh = null;
        }
        
        // Remove physics collider
        if (this.collider && this.physics) {
            this.physics.removeCollider(this.collider);
            this.collider = null;
        }
    }
} 